import { useState } from "react";
import { siteCategories, type ManhwaSite } from "@/mocks/manhwaData";

interface CategoriesProps {
  query: string;
  onNotify: (msg: string) => void;
}

export default function Categories({ query, onNotify }: CategoriesProps) {
  const [active, setActive] = useState(siteCategories[0].id);

  const trimmed = query.trim();
  let displayed: ManhwaSite[] = [];
  let resultTitle = "";

  if (trimmed) {
    const kw = trimmed.toLowerCase();
    const matched = siteCategories
      .flatMap((c) => c.sites)
      .filter((s) =>
        [s.name, s.desc, s.tags.join(" ")].join(" ").toLowerCase().includes(kw)
      );
    displayed = matched;
    resultTitle = `“${trimmed}” 的搜索结果（${matched.length} 个站点）`;
  } else {
    const cat = siteCategories.find((c) => c.id === active) ?? siteCategories[0];
    displayed = cat.sites;
    resultTitle = cat.name;
  }

  const handleEnter = (name: string) => {
    try {
      navigator.clipboard?.writeText(name);
    } catch {
      /* ignore */
    }
    onNotify(`已复制「${name}」入口信息`);
  };

  return (
    <section id="categories" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
            <i className="ri-grid-fill"></i>
            题材分类
          </span>
          <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">韩漫站点分类导航</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-foreground-600">
            按题材整理的韩漫网址入口，点击分类查看对应站点，或用上方搜索框筛选想看的题材。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {siteCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActive(cat.id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === cat.id && !trimmed
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 hover:bg-primary-100"
              }`}
            >
              <i className="ri-price-tag-3-line"></i>
              {cat.name}
              <span
                className={`rounded-full px-1.5 text-xs ${
                  active === cat.id && !trimmed ? "bg-background-50/20" : "bg-background-200 text-foreground-500"
                }`}
              >
                {cat.count}
              </span>
            </button>
          ))}
        </div>

        {trimmed ? (
          <div className="mx-auto mt-8 max-w-3xl rounded-lg border border-background-200 bg-background-50 px-5 py-4 text-center">
            <p className="text-sm text-foreground-700">{resultTitle}</p>
            {displayed.length === 0 && (
              <p className="mt-2 text-sm text-foreground-500">
                暂时没有匹配的站点，换个关键词试试，或通过「提交收录」告诉我们。
              </p>
            )}
          </div>
        ) : (
          <div className="mx-auto mt-8 grid max-w-5xl grid-cols-1 gap-5 sm:grid-cols-3">
            {displayed.map((site) => {
              const cat = siteCategories.find((c) => c.id === site.categoryId);
              return (
                <article
                  key={site.id}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
                >
                  <div className="relative h-28 w-full overflow-hidden">
                    <img
                      src={cat?.image}
                      alt={`${cat?.name}类韩漫站点${site.name}封面`}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-foreground-950/60 px-2.5 py-0.5 text-[10px] text-background-50 backdrop-blur">
                      {cat?.badge}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-foreground-950">{site.name}</h3>
                      <span className="flex items-center gap-1 text-xs font-medium text-accent-600">
                        <i className="ri-star-fill"></i>
                        {site.rating.toFixed(1)}
                      </span>
                    </div>
                    <p className="mt-2 flex-1 text-xs leading-relaxed text-foreground-600">{site.desc}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-secondary-700">{site.feature}</span>
                      <button
                        type="button"
                        onClick={() => handleEnter(site.name)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                      >
                        进入
                        <i className="ri-arrow-right-line"></i>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}